import { useRef } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { CUSTOM_MIME_TYPES } from '@/helpers/enums';
import prepareTranslate from '@/helpers/dictionary';
import vars from '../../config/vars';
import styles from './style.module.css';

const allowedExtensions = Object.values(CUSTOM_MIME_TYPES).flatMap(({ extensions }) => extensions);
const allowedExtensionsLabel = allowedExtensions.join(', ');

const UploadFilesButton = ({ onFilesSelected, label }) => {
  const inputRef = useRef(null);
  const t = prepareTranslate();
  const maxFileSizeInBytes = vars.maxFileSize * 1024 ** 2;

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      const validFiles = [];
      const oversizeNames = [];
      const invalidTypeNames = [];

      selectedFiles.forEach((file) => {
        const ext = `.${file.name.split('.').pop().toLowerCase()}`;
        if (file.size > maxFileSizeInBytes) {
          oversizeNames.push(file.name);
        } else if (!allowedExtensions.includes(ext)) {
          invalidTypeNames.push(file.name);
        } else {
          validFiles.push(file);
        }
      });

      if (oversizeNames.length > 0) {
        notifications.show({
          title: t('components.customDropZone.notification.invalidFileSize.title'),
          message: `${t('components.customDropZone.notification.invalidFileSize.message')} ${oversizeNames.join(', ')}`,
          autoClose: false,
        });
      }

      if (invalidTypeNames.length > 0) {
        notifications.show({
          title: t('components.customDropZone.notification.invalidFileFormat.title'),
          message: `${t('components.customDropZone.notification.invalidFileFormat.message')} ${invalidTypeNames.join(', ')}. ${t('components.customDropZone.notification.invalidFileFormat.allow')} ${allowedExtensionsLabel}`,
          autoClose: false,
        });
      }

      if (validFiles.length > 0) {
        onFilesSelected(validFiles);
      }

      e.target.value = null;
    }
  };

  return (
    <>
      <Button
        mt='1rem'
        variant="secondary"
        size='xs'
        leftSection={<IconPlus />}
        onClick={handleClick}
      >
        {label}
      </Button>
      <input
        type='file'
        ref={inputRef}
        onChange={handleChange}
        className={styles.input}
        accept={allowedExtensions.join(',')}
        multiple
      />
    </>
  );
};

UploadFilesButton.propTypes = {
  onFilesSelected: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

export default UploadFilesButton;
